/**
 * FareHistoryToggle — "Price history by fare type". Closed by default; the data is fetched
 * the first time it opens. Used under a leg's history strip and in each flight's fare ladder.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { tripsApi } from "@/lib/api";
import FareHistoryChart from "./FareHistoryChart";

interface Props {
  tripId: string;
  routeId: string;
  dedupeKey: string;
}

function Loaded({ tripId, routeId, dedupeKey }: Props) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["fare-history", tripId, routeId, dedupeKey],
    queryFn: () => tripsApi.getFareHistory(tripId, routeId, dedupeKey),
    staleTime: 5 * 60_000,
  });
  if (isLoading) return <p className="text-xs text-muted-foreground">Loading the checks.</p>;
  if (isError || !data) return <p className="text-xs text-muted-foreground">We could not load this history just now.</p>;
  return <FareHistoryChart view={data} />;
}

export default function FareHistoryToggle(props: Props) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex min-h-[44px] items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground sm:min-h-[28px]"
      >
        Price history by fare type
        <ChevronDown className={"h-3.5 w-3.5 transition-transform motion-reduce:transition-none " + (open ? "rotate-180" : "")} aria-hidden="true" />
      </button>
      {open && (
        <div className="mt-1 rounded-md border bg-card px-3 py-2 shadow-paper">
          <Loaded {...props} />
        </div>
      )}
    </div>
  );
}
